import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import HtmlEditor from "../../components/admin/HtmlEditor";
import ImageUpload from "../../components/admin/ImageUpload";
import { useSettings } from "../../context/SettingsContext";

const blank = {
  site_title: "Reptile Global",
  tagline: "",
  description: "",
  contact_email: "",
  contact_phone: "",
  address: "",
  social_links: { twitter: "", linkedin: "", instagram: "" },
  home_seo: { title: "", description: "", keywords: "", og_image: "", og_type: "website", og_url: "", twitter_card: "summary_large_image", canonical_url: "", json_ld: "" },
  about_content: "",
  services_content: "",
  ga_measurement_id: "",
  organization_schema: "",
  site_url: "",
};

const inputCls = "w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500 text-sm";
const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1.5">{label}</label>
    {children}
    {hint && <div className="text-xs text-zinc-400 mt-1">{hint}</div>}
  </div>
);

const AdminSettings = () => {
  const { refresh } = useSettings();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-settings").then((r) => setForm({
      ...blank,
      ...r.data,
      social_links: { ...blank.social_links, ...(r.data?.social_links || {}) },
      home_seo: { ...blank.home_seo, ...(r.data?.home_seo || {}) },
    })).finally(() => setLoading(false));
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updNested = (k1, k2, v) => setForm((f) => ({ ...f, [k1]: { ...f[k1], [k2]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/site-settings", form);
      toast.success("Saved");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-zinc-500">Loading…</div>;

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-black tracking-tight">Site Settings</h1>
        <button type="submit" disabled={saving} data-testid="admin-settings-save" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-md inline-flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
      </div>

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2 text-xs uppercase tracking-widest font-bold text-zinc-500">General</div>
        <Field label="Site title"><input className={inputCls} value={form.site_title} onChange={(e) => upd("site_title", e.target.value)} /></Field>
        <Field label="Tagline"><input className={inputCls} value={form.tagline} onChange={(e) => upd("tagline", e.target.value)} /></Field>
        <div className="md:col-span-2"><Field label="Description"><textarea className={`${inputCls} min-h-[80px]`} value={form.description} onChange={(e) => upd("description", e.target.value)} /></Field></div>
        <Field label="Site URL" hint="Used for sitemap.xml and canonical defaults"><input className={inputCls} value={form.site_url} onChange={(e) => upd("site_url", e.target.value)} placeholder="https://reptileglobal.com" /></Field>
        <Field label="GA4 Measurement ID" hint="Format: G-XXXXXXXXXX"><input className={inputCls} value={form.ga_measurement_id} onChange={(e) => upd("ga_measurement_id", e.target.value)} placeholder="G-XXXXXXXXXX" /></Field>
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2 text-xs uppercase tracking-widest font-bold text-zinc-500">Contact</div>
        <Field label="Contact email"><input className={inputCls} value={form.contact_email} onChange={(e) => upd("contact_email", e.target.value)} /></Field>
        <Field label="Contact phone"><input className={inputCls} value={form.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} /></Field>
        <div className="md:col-span-2"><Field label="Address"><input className={inputCls} value={form.address} onChange={(e) => upd("address", e.target.value)} /></Field></div>
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="md:col-span-3 text-xs uppercase tracking-widest font-bold text-zinc-500">Social</div>
        <Field label="Twitter"><input className={inputCls} value={form.social_links.twitter || ""} onChange={(e) => updNested("social_links", "twitter", e.target.value)} /></Field>
        <Field label="LinkedIn"><input className={inputCls} value={form.social_links.linkedin || ""} onChange={(e) => updNested("social_links", "linkedin", e.target.value)} /></Field>
        <Field label="Instagram"><input className={inputCls} value={form.social_links.instagram || ""} onChange={(e) => updNested("social_links", "instagram", e.target.value)} /></Field>
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2 text-xs uppercase tracking-widest font-bold text-zinc-500">Home page SEO</div>
        <Field label="Meta title"><input className={inputCls} value={form.home_seo.title || ""} onChange={(e) => updNested("home_seo", "title", e.target.value)} /></Field>
        <Field label="Keywords"><input className={inputCls} value={form.home_seo.keywords || ""} onChange={(e) => updNested("home_seo", "keywords", e.target.value)} /></Field>
        <div className="md:col-span-2"><Field label="Meta description"><textarea className={`${inputCls} min-h-[80px]`} value={form.home_seo.description || ""} onChange={(e) => updNested("home_seo", "description", e.target.value)} /></Field></div>
        <div className="md:col-span-2"><ImageUpload label="OG image (home)" value={form.home_seo.og_image || ""} onChange={(v) => updNested("home_seo", "og_image", v)} testId="home-og-upload" /></div>
        <Field label="OG type"><input className={inputCls} value={form.home_seo.og_type || ""} onChange={(e) => updNested("home_seo", "og_type", e.target.value)} placeholder="website" /></Field>
        <Field label="OG URL"><input className={inputCls} value={form.home_seo.og_url || ""} onChange={(e) => updNested("home_seo", "og_url", e.target.value)} /></Field>
        <Field label="Twitter card"><input className={inputCls} value={form.home_seo.twitter_card || ""} onChange={(e) => updNested("home_seo", "twitter_card", e.target.value)} placeholder="summary_large_image" /></Field>
        <Field label="Canonical URL"><input className={inputCls} value={form.home_seo.canonical_url || ""} onChange={(e) => updNested("home_seo", "canonical_url", e.target.value)} /></Field>
        <div className="md:col-span-2">
          <Field label="JSON-LD (Home)" hint="WebSite or WebPage schema">
            <textarea className={`${inputCls} min-h-[140px] font-mono text-xs`} value={form.home_seo.json_ld || ""} onChange={(e) => updNested("home_seo", "json_ld", e.target.value)} placeholder='{"@context":"https://schema.org","@type":"WebSite", ...}' />
          </Field>
        </div>
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 gap-5">
        <div className="text-xs uppercase tracking-widest font-bold text-zinc-500">Organization Schema (site-wide)</div>
        <Field label="JSON-LD Organization Schema" hint="Injected on every page (in addition to per-page schema)">
          <textarea className={`${inputCls} min-h-[160px] font-mono text-xs`} value={form.organization_schema} onChange={(e) => upd("organization_schema", e.target.value)} placeholder='{"@context":"https://schema.org","@type":"Organization","name":"Reptile Global","url":"https://reptileglobal.com","logo":"..." }' />
        </Field>
      </section>

      <section className="bg-white border border-zinc-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2 text-xs uppercase tracking-widest font-bold text-zinc-500">Page content</div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1.5">About page content (HTML)</label>
          <HtmlEditor value={form.about_content} onChange={(v) => upd("about_content", v)} minHeight={220} testId="about-content" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Services page content (HTML)</label>
          <HtmlEditor value={form.services_content} onChange={(v) => upd("services_content", v)} minHeight={220} testId="services-content" />
        </div>
      </section>
    </form>
  );
};

export default AdminSettings;
